import { createContext, useContext, useEffect, type ReactElement, type ReactNode } from 'react';
import { createTelemetry } from './telemetry';
import { type Telemetry, type TelemetryAttributes } from './types';

let fallback: Telemetry | null = null;

/** Process-wide no-op instance used outside a TelemetryProvider. */
export function getDefaultTelemetry(): Telemetry {
  if (!fallback) fallback = createTelemetry({ exporter: 'none', flushIntervalMs: 0 });
  return fallback;
}

const TelemetryContext = createContext<Telemetry | null>(null);

export interface TelemetryProviderProps {
  children: ReactNode;
  telemetry?: Telemetry;
}

export function TelemetryProvider({ children, telemetry }: TelemetryProviderProps): ReactElement {
  return <TelemetryContext.Provider value={telemetry ?? getDefaultTelemetry()}>{children}</TelemetryContext.Provider>;
}

export function useTelemetry(): Telemetry {
  return useContext(TelemetryContext) ?? getDefaultTelemetry();
}

/** Emits a `page.view` event when `name` changes (route-level usage). */
export function usePageView(name: string, attrs?: TelemetryAttributes): void {
  const telemetry = useTelemetry();
  const attrKey = JSON.stringify(attrs ?? {});
  useEffect(() => {
    const extra = JSON.parse(attrKey) as TelemetryAttributes;
    telemetry.event('page.view', { 'page.name': name, ...(typeof window !== 'undefined' ? { 'page.path': window.location.pathname } : {}), ...extra });
  }, [telemetry, name, attrKey]);
}
